import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

type CatalogIndexMetrics = {
  allComponents: number;
  ownedComponents: number;
  productionComponents: number;
  systems: number;
  templates: number;
};

const defaultMetrics: CatalogIndexMetrics = {
  allComponents: 0,
  ownedComponents: 0,
  productionComponents: 0,
  systems: 0,
  templates: 0,
};

const countEntities = async (
  catalogApi: CatalogApi,
  filter: Record<string, string | string[]>,
) => {
  const response = await catalogApi.queryEntities({
    filter,
    limit: 0,
  });

  return response.totalItems ?? 0;
};

export const useCatalogIndexMetrics = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const { error, loading, value } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const ownershipEntityRefs = identity.ownershipEntityRefs;

    const [
      allComponents,
      productionComponents,
      systems,
      templates,
      ownedComponents,
    ] = await Promise.all([
      countEntities(catalogApi, { kind: 'component' }),
      countEntities(catalogApi, {
        kind: 'component',
        'spec.lifecycle': 'production',
      }),
      countEntities(catalogApi, { kind: 'system' }),
      countEntities(catalogApi, { kind: 'template' }),
      ownershipEntityRefs.length
        ? countEntities(catalogApi, {
            kind: 'component',
            'relations.ownedBy': ownershipEntityRefs,
          })
        : Promise.resolve(0),
    ]);

    return {
      allComponents,
      ownedComponents,
      productionComponents,
      systems,
      templates,
    };
  }, [catalogApi, identityApi]);

  return {
    error,
    loading,
    metrics: value ?? defaultMetrics,
  };
};
